/** Settings of the retry delay. */
export interface BackoffPolicy {
  /** Delay after the first failure in milliseconds. */
  readonly initialDelayMs: number;
  /** Upper limit in milliseconds. */
  readonly maxDelayMs: number;
}

/** Default: 2 s, doubling, at most 5 min. */
export const defaultBackoffPolicy: BackoffPolicy = { initialDelayMs: 2_000, maxDelayMs: 300_000 };

/**
 * Computes the delay before the next attempt: exponential growth with "equal jitter" (between half
 * and the full delay) so that many devices coming online together do not retry in lockstep.
 *
 * @param failures - Consecutive failures (≥ 1).
 * @param policy - Backoff policy.
 * @param random - Random number in [0, 1) (injected for deterministic tests).
 * @returns Delay in milliseconds.
 */
export function backoffDelayMs(failures: number, policy: BackoffPolicy, random: number): number {
  const exponent = Math.min(Math.max(failures, 1) - 1, 30);
  const capped = Math.min(policy.maxDelayMs, policy.initialDelayMs * 2 ** exponent);
  const delay = Math.round(capped / 2 + (capped / 2) * random);

  return delay;
}
